import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { signalRService } from '../services/signalRService';

const LAST_SEEN_KEY = 'notif_last_seen';

/**
 * Notifications temps réel via SignalR (connexion partagée avec useSignalR).
 *
 * - S'abonne à "NotificationsUpdated" pour re-fetch immédiat dès qu'une réservation change.
 * - Polling 30 min en secours si SignalR est indisponible (tab inactif, réseau coupé).
 * - markAsSeen() → compteur tombe à 0 localement sans appel serveur.
 */
export const useNotifications = () => {
  const user = useSelector(s => s.auth.user);
  const [notifications, setNotifications] = useState([]);
  const [count, setCount]                 = useState(0);
  const [loading, setLoading]             = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) ?? new Date(0).toISOString();
      const { data } = await api.get('/notifications', { params: { lastSeen } });
      setNotifications(data.notifications ?? []);
      setCount(data.count ?? 0);
    } catch {
      // silent — les notifications ne bloquent jamais l'app
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsSeen = useCallback(() => {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    setCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, isNew: false })));
  }, []);

  // Fetch initial
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Abonnement SignalR via le singleton (connexion gérée par useSignalR dans AppShell)
  useEffect(() => {
    if (!user) return;

    const unsub = signalRService.on('NotificationsUpdated', fetchNotifications);
    const fallbackId = setInterval(fetchNotifications, 30 * 60 * 1000);

    return () => {
      unsub();
      clearInterval(fallbackId);
    };
  }, [user, fetchNotifications]);

  // Rétro-compatibilité pour tout code qui référence encore retoursDuJour
  const retoursDuJour = notifications.filter(n => n.type === 'RETOUR_J');

  return {
    notifications,
    retoursDuJour,
    count,
    loading,
    refresh: fetchNotifications,
    markAsSeen,
  };
};

export default useNotifications;
